var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { CustomEvents, MediaStartedPlayingEvent } from "./events-yl6bDZlK.js";
import { onScrollUpdate } from "./scroll-dispatcher-BIs-vu0Q.js";
const responsiveViewport = window.matchMedia("(min-width: 992px)");
const responsiveVideoInstances = /* @__PURE__ */ new Set();
function handleResponsiveViewportChange() {
  responsiveVideoInstances.forEach((instance) => instance._handleResponsiveViewportChange());
}
function registerResponsiveVideo(instance) {
  responsiveVideoInstances.add(instance);
  if (responsiveVideoInstances.size === 1) {
    responsiveViewport.addEventListener("change", handleResponsiveViewportChange);
  }
}
function unregisterResponsiveVideo(instance) {
  responsiveVideoInstances.delete(instance);
  if (responsiveVideoInstances.size === 0) {
    responsiveViewport.removeEventListener("change", handleResponsiveViewportChange);
  }
}
const _CustomVideo = class _CustomVideo extends HTMLElement {
  constructor() {
    super();
    this.autoplay = false;
    this.loop = false;
    this.isBackgroundVideo = false;
    this.hasManualControls = false;
    this._playbackActive = true;
    this._isInView = true;
    this._responsiveVariant = null;
    this._isResponsiveActive = true;
    this._isMediaReady = false;
    this.isPlaying = false;
    this.isInitialized = false;
  }
  connectedCallback() {
    this._readDatasetConfig();
    this._listingVisibilityManaged = !!this.closest("listing-component");
    if (this._listingVisibilityManaged || this._responsiveVariant) this._isInView = false;
    if (this._responsiveVariant) registerResponsiveVideo(this);
    this._initializeControlledPlaybackState();
    this._abortController = new AbortController();
    document.addEventListener(CustomEvents.mediaStartedPlaying, this._handleMediaStarted.bind(this), { signal: this._abortController.signal });
    this.coverImage = this.querySelector("[data-video-cover]");
    this.controls = this.querySelector("[data-video-controls]");
    this.toggleWrap = this.querySelector("[data-video-controls-buttons]");
    this.buttonPlay = this.querySelector(".vid-block-controls-play");
    this.buttonPause = this.querySelector(".vid-block-controls-pause");
    this._handlePointerMove = this._onPointerMove.bind(this);
    this._handlePointerLeave = this._handleLeave.bind(this);
    this._handleClick = () => {
      if (!this.isInitialized) return;
      this.toggleVideo();
    };
    if (this.hasManualControls && this.controls) {
      this.controls.addEventListener("click", this._handleClick);
      this.controls.addEventListener("pointermove", this._handlePointerMove, { passive: true });
      this.controls.addEventListener("mouseleave", this._handlePointerLeave);
    }
    if (this.hasManualControls && this.coverImage) {
      this.coverImage.addEventListener("click", this._handleClick);
    }
    if (this.isBackgroundVideo || this._listingVisibilityManaged || this._responsiveVariant) {
      this._handleVisibilityChange = () => {
        if (this.isBackgroundVideo) this._syncBackgroundPlayback();
        else if (this._listingVisibilityManaged) this._syncListingPlayback();
        else this._syncResponsivePlayback();
      };
      document.addEventListener("visibilitychange", this._handleVisibilityChange);
    }
    const tpl = this.querySelector("template");
    this._template = tpl;
    if (tpl || this._listingVisibilityManaged || this._responsiveVariant || this.isBackgroundVideo && this.isInitialized) {
      this._io = new IntersectionObserver(
        (entries, obs) => {
          const isInView = entries[0].isIntersecting;
          if (this.isBackgroundVideo || this._listingVisibilityManaged || this._responsiveVariant) {
            this._isInView = isInView;
          }
          if (this.isBackgroundVideo) {
            this._syncBackgroundPlayback();
          } else if (this._listingVisibilityManaged) {
            this._syncListingPlayback();
          } else if (this._responsiveVariant) {
            this._syncResponsivePlayback();
          } else if (isInView) {
            if (!this.isInitialized && !this._initScheduled) {
              this._scheduleVideoInit(tpl);
            }
            obs.disconnect();
          }
        },
        { threshold: this.isBackgroundVideo ? 0.01 : 0.25 }
      );
      this._io.observe(this);
    }
  }
  _readDatasetConfig() {
    this.autoplay = this.dataset.autoplay === "true";
    this.loop = this.dataset.loop === "true";
    this.isBackgroundVideo = this.dataset.backgroundVideo === "true";
    this.hasManualControls = this.dataset.controls === "true" && !this.isBackgroundVideo;
    this._playbackActive = this.dataset.playbackActive !== "false";
    const responsiveVariant = this.dataset.responsiveVariant;
    this._responsiveVariant = responsiveVariant === "desktop" || responsiveVariant === "mobile" ? responsiveVariant : null;
    this._isResponsiveActive = this._matchesResponsiveViewport();
    this._isInView = !this.isBackgroundVideo;
    if (!this.isInitialized) {
      this.isPlaying = this.autoplay && this._playbackActive;
    }
  }
  isInteractionReady() {
    if (this._responsiveVariant && !this._isResponsiveActive) return true;
    return !this.hasManualControls || this.isInitialized;
  }
  prepareInteraction() {
    var _a;
    if (this._responsiveVariant && !this._isResponsiveActive) return true;
    if (this.hasManualControls && !this.isInitialized && this._template) {
      if (!this._listingVisibilityManaged && !this._responsiveVariant) (_a = this._io) == null ? void 0 : _a.disconnect();
      this._initScheduled = false;
      this._initVideo(this._template);
    }
    return this.isInteractionReady();
  }
  disconnectedCallback() {
    var _a, _b, _c, _d, _e;
    if ((this._listingVisibilityManaged || this._responsiveVariant) && this.isInitialized) {
      this._pause({ preservePlaybackActive: true });
    }
    (_a = this._abortController) == null ? void 0 : _a.abort();
    (_b = this.controls) == null ? void 0 : _b.removeEventListener("click", this._handleClick);
    (_c = this.controls) == null ? void 0 : _c.removeEventListener("pointermove", this._handlePointerMove);
    (_d = this.controls) == null ? void 0 : _d.removeEventListener("mouseleave", this._handlePointerLeave);
    if (this._unbindScroll) {
      this._unbindScroll();
      this._unbindScroll = null;
      this._scrollBound = null;
    }
    this._lastX = null;
    clearTimeout(this._leaveCleanup);
    clearTimeout(this._hideTimer);
    (_e = this._io) == null ? void 0 : _e.disconnect();
    if (this._responsiveVariant) unregisterResponsiveVideo(this);
    if (this._handleVisibilityChange) {
      document.removeEventListener("visibilitychange", this._handleVisibilityChange);
    }
    this._msgHandler && window.removeEventListener("message", this._msgHandler);
  }
  _handleMediaStarted(event) {
    var _a;
    if (this.isBackgroundVideo) return;
    if (((_a = event == null ? void 0 : event.detail) == null ? void 0 : _a.resource) === this) return;
    if (!this.isInitialized) return;
    if (!this.isPlaying) return;
    this._pause();
  }
  // ── Video lifecycle ──────────────────────────────────────
  _matchesResponsiveViewport() {
    if (this._responsiveVariant === "desktop") return responsiveViewport.matches;
    if (this._responsiveVariant === "mobile") return !responsiveViewport.matches;
    return true;
  }
  _handleResponsiveViewportChange() {
    const isActive = this._matchesResponsiveViewport();
    if (isActive === this._isResponsiveActive) return;
    this._isResponsiveActive = isActive;
    if (this.isBackgroundVideo) {
      this._syncBackgroundPlayback();
    } else if (this._listingVisibilityManaged) {
      this._syncListingPlayback();
    } else {
      this._syncResponsivePlayback();
    }
  }
  _initializeControlledPlaybackState() {
    if (!this.isBackgroundVideo || this.hasAttribute("data-playback-active")) return;
    const featuredBackground = this.closest(".sc-featured-products-slideshow-bg-item");
    if (featuredBackground) {
      this._setPlaybackActive(featuredBackground.classList.contains("is-active"));
      this.isPlaying = this.autoplay && this._playbackActive;
      return;
    }
    const slide = this.closest(".slide-item");
    const slideshow = slide == null ? void 0 : slide.closest("slideshow-component");
    if (!slideshow) return;
    this._setPlaybackActive(slideshow.querySelector(".slide-item") === slide);
    this.isPlaying = this.autoplay && this._playbackActive;
  }
  _scheduleVideoInit(tpl) {
    this._initScheduled = true;
    const init = () => {
      this._initScheduled = false;
      if (!this.isConnected || this.isInitialized) return;
      if (this._responsiveVariant && !this._isResponsiveActive) return;
      if ((this.isBackgroundVideo || this._listingVisibilityManaged || this._responsiveVariant) && (!this._playbackActive || !this._isInView || document.hidden)) return;
      this._initVideo(tpl);
    };
    if (this.dataset.deferInit === "true" && document.readyState !== "complete") {
      window.addEventListener("load", () => {
        if (typeof requestIdleCallback !== "undefined") {
          requestIdleCallback(init, { timeout: 2e3 });
        } else {
          setTimeout(init, 100);
        }
      }, { once: true });
    } else {
      init();
    }
  }
  _initVideo(tpl) {
    if (this._responsiveVariant && !this._isResponsiveActive) return;
    const el = tpl.content.querySelector("iframe, video");
    if (!el) return;
    this._media = el.cloneNode(true);
    this._isMediaReady = false;
    if (this._responsiveVariant && this._media.tagName === "IFRAME") {
      this._media.addEventListener("load", () => {
        if (!this._isResponsiveActive || !this._playbackActive || document.hidden) {
          this._pause({ preservePlaybackActive: true });
        }
      }, { once: true });
    }
    if (this.isBackgroundVideo) {
      this._media.setAttribute("aria-hidden", "true");
      this._media.setAttribute("tabindex", "-1");
      if (this._media.tagName === "VIDEO") {
        this._media.muted = true;
        this._media.defaultMuted = true;
        this._media.loop = true;
        this._media.playsInline = true;
        this._media.controls = false;
        this._media.addEventListener("playing", () => this._setPlayUI(true));
      }
    }
    const onReady = () => {
      var _a;
      this._isMediaReady = true;
      if (this.isPlaying) {
        (_a = this.coverImage) == null ? void 0 : _a.classList.add("hidden");
      }
    };
    this._media.addEventListener(
      this._media.tagName === "IFRAME" ? "load" : "loadedmetadata",
      onReady,
      { once: true }
    );
    if (!this.loop) this._listenForEnd();
    tpl.insertAdjacentElement("afterend", this._media);
    tpl.remove();
    this._template = null;
    this.isInitialized = true;
    this.classList.add("loaded");
    if (this.isBackgroundVideo) {
      this._syncBackgroundPlayback();
    } else if (this._listingVisibilityManaged) {
      this._syncListingPlayback();
    } else if (this._responsiveVariant) {
      this._syncResponsivePlayback();
    } else if (this.autoplay && this._playbackActive) {
      this._play(true);
    }
  }
  toggleVideo() {
    this.isPlaying ? this._pause() : this._play();
  }
  playMedia() {
    this._setPlaybackActive(true);
    if (this.isBackgroundVideo) {
      this._syncBackgroundPlayback();
      return;
    }
    if (this._listingVisibilityManaged) {
      this._syncListingPlayback();
      return;
    }
    if (this._responsiveVariant) {
      this._syncResponsivePlayback();
      return;
    }
    if (this.isInitialized && this.autoplay) this._play(true);
  }
  pauseMedia() {
    this._setPlaybackActive(false);
    if (this.isInitialized) {
      this._pause({ preservePlaybackActive: true });
    } else {
      this._setPauseUI();
    }
  }
  _setPlaybackActive(isActive) {
    this._playbackActive = isActive;
    const value = String(isActive);
    if (this.dataset.playbackActive !== value) {
      this.dataset.playbackActive = value;
    }
  }
  _syncBackgroundPlayback() {
    if (!this.isBackgroundVideo || !this.autoplay) return;
    if (this._playbackActive && this._isInView && this._isResponsiveActive && !document.hidden) {
      if (!this.isInitialized) {
        if (this._template && !this._initScheduled) {
          this._scheduleVideoInit(this._template);
        }
        return;
      }
      this._play(true);
    } else if (this.isInitialized) {
      this._pause({ preservePlaybackActive: true });
    }
  }
  _syncListingPlayback() {
    var _a;
    if (!this._listingVisibilityManaged) return;
    if (!this._playbackActive || !this._isInView || !this._isResponsiveActive || document.hidden) {
      if (this.isInitialized) {
        this._pause({ preservePlaybackActive: true });
      }
      return;
    }
    if (!this.isInitialized) {
      if (this._template && !this._initScheduled) {
        this._scheduleVideoInit(this._template);
      }
      return;
    }
    const nativeVideoPaused = ((_a = this._media) == null ? void 0 : _a.tagName) === "VIDEO" && this._media.paused;
    if (this.autoplay && (!this.isPlaying || nativeVideoPaused)) {
      this._play(true);
    }
  }
  _syncResponsivePlayback() {
    var _a;
    if (!this._responsiveVariant) return;
    if (!this._isResponsiveActive || document.hidden) {
      if (this.isInitialized) {
        this._pause({ preservePlaybackActive: true });
      }
      return;
    }
    if (!this._isInView) return;
    if (!this.isInitialized) {
      if (this._template && !this._initScheduled) {
        this._scheduleVideoInit(this._template);
      }
      return;
    }
    const nativeVideoPaused = ((_a = this._media) == null ? void 0 : _a.tagName) === "VIDEO" && this._media.paused;
    const hasStarted = this.hasAttribute("data-started");
    if (this.autoplay && this._playbackActive && (!hasStarted || !this.isPlaying || nativeVideoPaused)) {
      this._play(true);
    } else if (!this.autoplay && !this.isPlaying) {
      this._setPauseUI();
    }
  }
  _play(isInit = false) {
    var _a, _b;
    if (this._responsiveVariant && !this._isResponsiveActive) return;
    this._setPlaybackActive(true);
    if (!this._media) return;
    if (this._media.tagName === "IFRAME") {
      const type = this._media.dataset.videoType;
      (_b = this._media.contentWindow) == null ? void 0 : _b.postMessage((_a = _CustomVideo.VIDEO_COMMANDS[type]) == null ? void 0 : _a.play, "*");
      this._setPlayUI(!isInit || this._isMediaReady);
      if (!isInit) {
        setTimeout(() => {
          if (this.isPlaying) this.dispatchEvent(new MediaStartedPlayingEvent(this));
        }, 0);
      }
    } else {
      const playPromise = this._media.play();
      if (playPromise !== void 0) {
        playPromise.then(() => {
          this._setPlayUI(true);
          if (!isInit) this.dispatchEvent(new MediaStartedPlayingEvent(this));
        }).catch((err) => {
          this._setPauseUI();
        });
      } else {
        this._setPlayUI(true);
      }
    }
  }
  _pause({ preservePlaybackActive = false } = {}) {
    var _a, _b;
    if (!preservePlaybackActive) this._setPlaybackActive(false);
    if (!this._media) {
      this._setPauseUI();
      return;
    }
    if (this._media.tagName === "IFRAME") {
      const type = this._media.dataset.videoType;
      (_b = this._media.contentWindow) == null ? void 0 : _b.postMessage((_a = _CustomVideo.VIDEO_COMMANDS[type]) == null ? void 0 : _a.pause, "*");
    } else {
      this._media.pause();
    }
    this._setPauseUI();
  }
  _setPlayUI(hideCoverImmediately = true) {
    var _a;
    this.isPlaying = true;
    this.dataset.started = "true";
    if (hideCoverImmediately) {
      (_a = this.coverImage) == null ? void 0 : _a.classList.add("hidden");
    }
    if (this.hasManualControls) {
      this.toggleButtonPlayPause();
      this._showControls();
      this._resetHideTimer();
    }
  }
  _setPauseUI() {
    this.isPlaying = false;
    if (this.hasManualControls) {
      this.toggleButtonPlayPause();
      this._showControls();
      clearTimeout(this._hideTimer);
    }
  }
  // ── Button / controls ────────────────────────────────────
  toggleButtonPlayPause() {
    var _a, _b;
    (_a = this.buttonPlay) == null ? void 0 : _a.classList.toggle("show", !this.isPlaying);
    (_b = this.buttonPause) == null ? void 0 : _b.classList.toggle("show", this.isPlaying);
  }
  _showControls() {
    var _a;
    (_a = this.toggleWrap) == null ? void 0 : _a.classList.remove("force-hide");
  }
  _hideControls() {
    var _a;
    if (!this.isPlaying) return;
    (_a = this.toggleWrap) == null ? void 0 : _a.classList.add("force-hide");
  }
  _resetHideTimer() {
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => this._hideControls(), 1e3);
  }
  // ── Cursor-follow (WAAPI stacking) ──────────────────────
  _updateTogglePosition() {
    if (!this.toggleWrap || this._lastX == null) return;
    const rect = (this.closest(".vid-block-content") || this.controls).getBoundingClientRect();
    this.toggleWrap.animate(
      { transform: `translate3d(${this._lastX - rect.left - rect.width / 2}px, ${this._lastY - rect.top - rect.height / 2}px, 0)` },
      { duration: 3200, fill: "forwards", easing: "cubic-bezier(0.16, 1, 0.3, 1)" }
    );
  }
  _onPointerMove(e) {
    if (!this.toggleWrap) return;
    if (this.toggleWrap.classList.contains("force-hide")) this._showControls();
    clearTimeout(this._leaveCleanup);
    this._lastX = e.clientX;
    this._lastY = e.clientY;
    if (!this._unbindScroll) {
      this._scrollBound = this._updateTogglePosition.bind(this);
      this._unbindScroll = onScrollUpdate(this._scrollBound);
    }
    this._updateTogglePosition();
    this._resetHideTimer();
  }
  _handleLeave() {
    if (this._unbindScroll) {
      this._unbindScroll();
      this._unbindScroll = null;
      this._scrollBound = null;
    }
    this._lastX = null;
    if (this.toggleWrap) {
      this.toggleWrap.animate(
        { transform: "translate3d(0, 0, 0)" },
        { duration: 2400, fill: "forwards", easing: "cubic-bezier(0.16, 1, 0.3, 1)" }
      );
      this._leaveCleanup = setTimeout(() => {
        var _a;
        (_a = this.toggleWrap) == null ? void 0 : _a.getAnimations().forEach((a) => {
          if (a.playState === "finished") a.cancel();
        });
      }, 3e3);
    }
  }
  // ── Video end detection ──────────────────────────────────
  _listenForEnd() {
    if (!this._media) return;
    if (this._media.tagName === "IFRAME") {
      this._media.addEventListener("load", () => {
        var _a, _b;
        const type = this._media.dataset.videoType;
        (_b = this._media.contentWindow) == null ? void 0 : _b.postMessage((_a = _CustomVideo.VIDEO_COMMANDS[type]) == null ? void 0 : _a.ended, "*");
      }, { once: true });
      this._msgHandler = (event) => {
        var _a, _b;
        try {
          const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
          if (event.origin === "https://www.youtube.com" && data.event === "infoDelivery") {
            if (((_a = data.info) == null ? void 0 : _a.duration) > 0 && data.info.currentTime >= data.info.duration - 1) {
              this._onVideoEnd();
            }
            if (((_b = data.info) == null ? void 0 : _b.playerState) === 1) {
              if (!this.isPlaying) this._setPlayUI(true);
              this.dispatchEvent(new MediaStartedPlayingEvent(this));
            }
          }
          if (/^https?:\/\/player\.vimeo\.com/.test(event.origin)) {
            if (data.event === "ended") {
              this._onVideoEnd();
            }
            if (data.event === "play") {
              if (!this.isPlaying) this._setPlayUI(true);
              this.dispatchEvent(new MediaStartedPlayingEvent(this));
            }
          }
        } catch {
        }
      };
      window.addEventListener("message", this._msgHandler);
    } else {
      this._media.addEventListener("ended", () => this._onVideoEnd());
      this._media.addEventListener("play", () => {
        if (!this.isPlaying) this._setPlayUI(true);
        this.dispatchEvent(new MediaStartedPlayingEvent(this));
      });
      this._media.addEventListener("pause", () => {
        var _a, _b;
        this.isPlaying = false;
        if (this.dataset.started === "true") {
          (_a = this.buttonPlay) == null ? void 0 : _a.classList.remove("show");
          (_b = this.buttonPause) == null ? void 0 : _b.classList.remove("show");
        } else {
          this.toggleButtonPlayPause();
        }
      });
    }
  }
  _onVideoEnd() {
    this._setPauseUI();
  }
};
__publicField(_CustomVideo, "VIDEO_COMMANDS", {
  youtube: {
    play: '{"event":"command","func":"playVideo","args":""}',
    pause: '{"event":"command","func":"pauseVideo","args":""}',
    ended: '{"event": "listening"}'
  },
  vimeo: {
    play: '{"method":"play"}',
    pause: '{"method":"pause"}',
    ended: '{"method": "addEventListener", "value": "ended"}'
  }
});
let CustomVideo = _CustomVideo;
if (!customElements.get("custom-video")) {
  customElements.define("custom-video", CustomVideo);
}
export {
  CustomVideo
};
